/**
 * <kit-configurator>
 * ----------------------------------------------------------------------
 * UI + cart layer for the kit-configurator PDP section (sections/kit-configurator.liquid).
 * Base and Size are single cascading selectors; Color is 1-4 independent
 * role pickers (Primary/Secondary/Third/Fourth yarn) — all driven entirely
 * by one JSON blob embedded server-side at render time. No fetches, no
 * further Liquid/Storefront API calls after page load except the
 * add-to-cart POST.
 *
 * Every UI update (selection change, price, invalid-state, add-to-cart)
 * funnels through resolveSelection() — one place that decides what the
 * current Base/Size/per-role-Color resolves to and whether it's valid, so
 * the summary text, the price, the data-kit-invalid flag, and the
 * add-to-cart click handler can never disagree with each other.
 * 
 * Add-to-cart posts a variable-length payload straight to /cart/add.js (kit
 * variant + one matched yarn variant per active color role, in one request)
 * using window.routes.cart_add_url (defined theme-wide in
 * snippets/theme-strings.liquid) and, on success, dispatches the theme's
 * global "theme-cart:updated" event — the same event component-product-form
 * (theme.js) dispatches — so the existing cart drawer/header icon update
 * themselves with no custom code here.
 *
 * Embedded JSON shape (see the <script type="application/json" data-kit-configurator>
 * tag rendered inside this element, built in sections/kit-configurator.liquid):
 *
 * {
 *   "bases": {
 *     "<Base option value, e.g. 'Farm Friends DK'>": {
 *       "yarnProductId": Number,
 *       "yarnProductTitle": String,
 *       "colors": {         // one independently-filtered list per color role —
 *         "primary": [],    // custom.kit_allowed_colors/_2/_3/_4 (optional,
 *         "second": [],     // product-level) restrict each role's list
 *         "third": [],      // independently in sections/kit-configurator.liquid;
 *         "fourth": []      // no restriction on a role means its list is
 *       }                   // every color on the linked yarn product.
 *         // Each entry: { "name": String, "variantId": Number|null, "price": Number|null, "swatch": String|null, "imageId": Number|null }
 *         // "price" is the yarn variant's price in the shop's smallest
 *         // currency unit (cents for USD) — same unit snippets/price.liquid's
 *         // `money` filter expects. "swatch" is a pre-built CSS value
 *         // ("rgb(r,g,b)" or "url(...)"), dropped straight into
 *         // --swatch-background. "imageId" is the real Shopify image id for
 *         // this color's variant image — matches the data-media-id rendered
 *         // in the gallery by snippets/product-media-gallery.liquid's
 *         // extra_media_variants loop (see updateGalleryMedia() below). All
 *         // null mean the underlying yarn variant/swatch/image couldn't be
 *         // resolved server-side (see the invalid-state handling in
 *         // resolveSelection() below).
 *     },
 *     ...
 *   },
 *   "sizes": [
 *     {
 *       "base": String,       // matches a key in bases{}
 *       "dimensions": [       // every non-Base option on this variant, in option order
 *         { "name": String, "value": String } // e.g. { "name": "Length", "value": "Cropped" }
 *         // Kits vary in how many dimensions they carry (Waystone: Length +
 *         // Finished Bust; Rift: Finished Bust only, with units baked into
 *         // the option value itself, e.g. "40\""). Empty array for a kit
 *         // with no dimensions at all (one variant per Base) — renderSizes()
 *         // falls back to a "Standard" label in that case.
 *       ],
 *       "kitVariantId": Number, // the kit product's own variant id for this Base + dimensions combo
 *       "amounts": {         // one key per color role; null = this kit doesn't use that role
 *         "primary": Number|null, // kit.primary_amount.value for this variant
 *         "second": Number|null,  // kit.second_amount.value for this variant
 *         "third": Number|null,   // kit.third_amount.value for this variant
 *         "fourth": Number|null   // kit.fourth_amount.value for this variant
 *       }
 *     },
 *     ...
 *   ],
 *   "roleLabels": {         // resolved once in Liquid from custom.kit_label_1-4
 *     "primary": String,    // (product-level, optional) — falls back to the
 *     "second": String,     // generic "Primary yarn"/"Secondary yarn"/"Third
 *     "third": String,      // yarn"/"Fourth yarn" strings when a kit doesn't
 *     "fourth": String      // set a custom label. Single source of truth —
 *   }                       // this.roles below just reads these, no fallback
 *                           // logic duplicated here.
 * }
 *
 * One "bases" entry exists per distinct Base (deduplicated server-side —
 * see sections/kit-configurator.liquid), but "sizes" has one entry per kit
 * variant (every dimension combination, for every Base).
 */
class KitConfigurator extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      dataScript: "[data-kit-configurator]",
      baseSelect: "[data-kit-base]",
      sizeSelect: "[data-kit-size]",
      price: "[data-kit-price]",
      summary: "[data-kit-summary]",
      addToCartButton: "[data-kit-add-to-cart]",
    };

    this.data = JSON.parse(this.querySelector(this.selectors.dataScript).textContent);
    this.instanceId = this.dataset.sectionId;
    this.swatchShape = this.dataset.swatchShape || "round";
    this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";

    // Labels come from this.data.roleLabels, resolved once in Liquid from the
    // optional custom.kit_label_1-4 product metafields (falls back to the
    // generic "Primary yarn" etc. strings there, not here) — see the JSDoc
    // shape comment above. Keeping the fallback in one place (Liquid) means
    // this array and the server-rendered <legend> text can't drift apart.
    this.roles = [
      { key: "primary", label: this.data.roleLabels.primary },
      { key: "second", label: this.data.roleLabels.second },
      { key: "third", label: this.data.roleLabels.third },
      { key: "fourth", label: this.data.roleLabels.fourth },
    ];

    this.roleFieldsets = {};
    this.colorLists = {};
    this.roleColorLabels = {};
    this.querySelectorAll("[data-kit-role]").forEach((fieldset) => {
      const role = fieldset.dataset.kitRole;
      this.roleFieldsets[role] = fieldset;
      this.colorLists[role] = fieldset.querySelector("[data-kit-color-list]");
      this.roleColorLabels[role] = fieldset.querySelector("[data-kit-role-color]");
    });

    this.baseSelect = this.querySelector(this.selectors.baseSelect);
    this.sizeSelect = this.querySelector(this.selectors.sizeSelect);
    this.priceEl = this.querySelector(this.selectors.price);
    this.summary = this.querySelector(this.selectors.summary);
    this.addToCartButton = this.querySelector(this.selectors.addToCartButton);

    // Shared with the media gallery via the enclosing <section> — see
    // sections/kit-configurator.liquid's data-media-layout-desktop/mobile
    // attributes and updateGalleryMedia() below.
    this.productSection = this.closest("section");
    this.mediaGallery = this.productSection?.querySelector("component-product-media-gallery");

    this.baseSelect.addEventListener("change", () => this.handleBaseChange());
    this.sizeSelect.addEventListener("change", () => this.updateSummary());
    // Color swatches are rebuilt on every Base change (see renderColors), so a
    // single delegated listener per role's container is simpler than
    // re-binding per-radio listeners each time. Each role's listener records
    // itself as the most recently changed role (read by updateSummary() to
    // decide which role's image the gallery should reflect) before
    // re-deriving everything else.
    this.roles.forEach(({ key }) => {
      this.colorLists[key].addEventListener("change", () => {
        this.lastChangedRole = key;
        this.updateSummary();
      });
    });
    this.addToCartButton.addEventListener("click", () => this.handleAddToCart());

    // Initial render — Base select already has its selected option from Liquid
    // (defaults to product.selected_or_first_available_variant.option1) — this
    // is also what makes the price/summary populate immediately on page load
    // rather than staying blank until the shopper first touches a selector.
    // suppressGallerySwap skips just this first updateGalleryMedia() call, so
    // the gallery keeps showing the kit product's own featured media on load
    // instead of jumping straight to the default color's yarn image — real
    // Base/Color changes below still swap it as normal.
    // Which role's color image the gallery should reflect — defaults to
    // primary (matches the original single-role behavior) and is reset back
    // to primary on every Base change, since renderColors() re-selects each
    // role's first swatch at that point anyway. Read by updateSummary().
    this.lastChangedRole = "primary";

    this.suppressGallerySwap = true;
    this.handleBaseChange();
    this.suppressGallerySwap = false;
  }

  handleBaseChange() {
    this.lastChangedRole = "primary";
    this.roles.forEach(({ key }) => this.renderColors(this.baseSelect.value, key));
    this.renderSizes(this.baseSelect.value);
    this.updateSummary();
  }

  /**
   * Rebuilds the Color swatch list for ONE role (e.g. "second") under the
   * given Base. Called once per role from handleBaseChange(). Radio group
   * name is role-scoped (kit-color-${role}-${instanceId}) so selecting a
   * color in one role's fieldset never clears another role's selection —
   * each role's [data-kit-color-list] is otherwise built identically to
   * the original single-role version. Markup/classes intentionally mirror
   * snippets/swatch.liquid (swatch__input / swatch__label / swatch__swatch /
   * --swatch-background) so these render pixel-identical to every other
   * PDP's swatches and pick up the existing theme.css rules (:checked
   * state, hover, shape) for free.
   */
  renderColors(baseName, role) {
    const base = this.data.bases[baseName];
    const colorList = this.colorLists[role];
    colorList.innerHTML = "";
    if (!base) return;

    base.colors[role].forEach((color, index) => {
      const inputId = `kit-color-${role}-${this.instanceId}-${index}`;

      const input = document.createElement("input");
      input.type = "radio";
      input.className = "swatch__input visually-hidden";
      input.name = `kit-color-${role}-${this.instanceId}`;
      input.id = inputId;
      // Looked back up via colorIndex in resolveSelection() rather than parsed
      // back out of .value — color.variantId can be null (unresolved yarn
      // variant match) and DOM attribute values silently stringify null to
      // the literal text "null", which would defeat the missing-data check.
      input.dataset.colorIndex = String(index);
      input.value = color.variantId != null ? color.variantId : "";
      input.setAttribute("aria-label", color.name);
      if (index === 0) input.checked = true;

      // Input then label as immediate siblings (not label-wraps-input) —
      // required for theme.css's `.swatch__input:checked + .swatch__label`
      // sibling selector to apply.
      const label = document.createElement("label");
      label.className = "swatch__label";
      label.title = color.name;
      label.setAttribute("for", inputId);

      const swatch = document.createElement("span");
      if (color.swatch) {
        swatch.className = `swatch__swatch swatch__swatch--shape-${this.swatchShape}`;
        swatch.style.setProperty("--swatch-background", color.swatch);
      } else {
        swatch.className = `swatch__swatch swatch__swatch--unavailable swatch__swatch--shape-${this.swatchShape}`;
      }
      label.appendChild(swatch);

      colorList.append(input, label);
    });
  }

  /** Repopulates the Size select with every dimension combo for the given Base. */
  renderSizes(baseName) {
    this.sizeSelect.innerHTML = "";

    // Kept on the instance (not re-filtered from this.data.sizes every time)
    // so resolveSelection() can look a selected <option> back up by index
    // without re-deriving the same filtered list, and without round-tripping
    // amounts{} through a DOM dataset string (see renderColors for why that's
    // unsafe when the underlying value can be null).
    this.currentSizes = this.data.sizes.filter((size) => size.base === baseName);

    this.currentSizes.forEach((size, index) => {
      const option = document.createElement("option");
      option.value = size.kitVariantId;
      option.dataset.sizeIndex = String(index);
      // Raw dimension values joined as-is — no units injected here, since
      // some kits bake them into the option value (Rift: `40"`) and some
      // don't (Waystone: `36.5`). A kit with zero dimensions (single
      // variant per Base) falls back to a plain "Standard" label.
      option.textContent = size.dimensions.length
        ? size.dimensions.map((dimension) => dimension.value).join(" — ")
        : "Standard";
      this.sizeSelect.appendChild(option);
    });
  }

  /**
   * Single source of truth for the current full selection. A role is
   * "active" only if this size's amounts[role] is non-null (see
   * sections/kit-configurator.liquid Task 2) — an inactive role is not a
   * validation failure, it's simply not part of this kit and contributes
   * nothing to price or the cart payload.
   */
  resolveSelection() {
    const base = this.data.bases[this.baseSelect.value];
    const sizeOption = this.sizeSelect.selectedOptions[0];
    const sizeIndex = sizeOption ? Number(sizeOption.dataset.sizeIndex) : null;
    const size = sizeIndex !== null ? this.currentSizes[sizeIndex] : null;

    const missing = [];
    if (!base) missing.push("no yarn product resolved for this Base");
    if (!size) missing.push("no size selected");

    const roles = this.roles.map(({ key, label }) => {
      const amount = size ? size.amounts[key] : null;
      const active = Number.isFinite(amount) && amount > 0;
      if (!active) return { role: key, label, active: false, amount: null, color: null, missing: [] };

      const checkedColor = this.colorLists[key].querySelector("input:checked");
      const colorIndex = checkedColor ? Number(checkedColor.dataset.colorIndex) : null;
      const color = base && colorIndex !== null ? base.colors[key][colorIndex] : null;

      const roleMissing = [];
      if (!color) roleMissing.push(`no color selected for ${label}`);
      else {
        if (color.variantId == null) roleMissing.push(`no matching yarn variant for "${color.name}" (${label})`);
        if (!Number.isFinite(color.price)) roleMissing.push(`missing price for "${color.name}" (${label})`);
      }
      if (roleMissing.length) missing.push(...roleMissing);

      return { role: key, label, active: true, amount, color, missing: roleMissing };
    });

    return { base, size, roles, missing, isValid: missing.length === 0 };
  }

  /**
   * Drives the price + summary text + data-kit-invalid + add-to-cart
   * disabled state from one resolveSelection() call. If the resolved
   * combination is missing kit metafield data, no price is shown (a plain
   * "—" rather than a stale or zero amount) and the reason is surfaced in
   * the summary area using the same error styling as the theme's native
   * add-to-cart errors.
   */
  updateSummary() {
    const { roles, missing, isValid } = this.resolveSelection();

    roles.forEach(({ role, active, color }) => {
      this.roleFieldsets[role].hidden = !active;
      // Mirrors the legend text to the currently-selected swatch (e.g. "Main
      // Color: Piglet") — blank when the role is inactive or no color is
      // resolved yet, same active/color check updateSummary() uses for price.
      const colorLabel = this.roleColorLabels[role];
      if (colorLabel) colorLabel.textContent = active && color ? `: ${color.name}` : "";
    });

    this.toggleAttribute("data-kit-invalid", !isValid);
    this.addToCartButton.disabled = !isValid;
    this.addToCartButton.setAttribute("aria-disabled", String(!isValid));

    this.summary.hidden = false;

    if (!isValid) {
      this.priceEl.textContent = "—";
      this.summary.classList.add("product-form__error-message-text");
      this.summary.textContent = `Cannot add to cart yet — ${missing.join("; ")}. Check kit.primary_amount / kit.second_amount / kit.third_amount / kit.fourth_amount and kit.primary_product on this variant.`;
      return;
    }

    const activeRoles = roles.filter((r) => r.active);
    const totalCents = activeRoles.reduce((sum, r) => sum + r.color.price * r.amount, 0);
    this.priceEl.textContent = this.formatMoney(totalCents);

    this.summary.classList.remove("product-form__error-message-text");
    this.summary.textContent = activeRoles
      .map((r) => `${r.amount} skein(s) of ${r.color.name} (${r.label})`)
      .join("; ");

    if (!this.suppressGallerySwap) {
      // Reflect whichever role's swatch the shopper most recently touched
      // (this.lastChangedRole), not always Primary — falls back to the
      // first active role if that role isn't currently active (e.g. its
      // fieldset got hidden by a Size change after the shopper picked it).
      const galleryRole = activeRoles.find((r) => r.role === this.lastChangedRole) || activeRoles[0];
      this.updateGalleryMedia(galleryRole.color.imageId);
    }
  }

  /**
   * Swaps the selected yarn color's image into the active gallery position,
   * mirroring the same id-based mechanism sections/main-product.liquid's
   * native variant picker uses on <component-product-media-gallery> (see
   * component-product-variant-selectors / section-product in theme.js) —
   * just triggered from this element's own Color selection instead of a
   * native variant change, since the image belongs to a different product
   * (the yarn) than the one this section's variant picker would track.
   * Picks the method by current layout exactly like theme.js's
   * renderFromFetch() does, except the grid ("2_column"/"1_column") case
   * calls updateProductImage() directly instead of a server re-fetch — that
   * method already existed on the gallery class with no caller before this.
   */
  updateGalleryMedia(imageId) {
    if (imageId == null || !this.mediaGallery || !this.productSection) return;

    const mediaId = `[data-media-id="${imageId}"]`;
    if (!this.mediaGallery.querySelector(mediaId)) return;

    const isDesktop = ["desktop", "desktop-wide", "desktop-x-wide"].includes(
      window.theme.breakPoints.currentBreakpoint
    );
    const layout = isDesktop
      ? this.productSection.dataset.mediaLayoutDesktop
      : this.productSection.dataset.mediaLayoutMobile;

    if (layout === "swiper") {
      this.mediaGallery.slideToActiveMedia(imageId);
    } else if (layout === "thumbs") {
      this.mediaGallery.updateActiveMedia(imageId);
      this.mediaGallery.updateActiveThumb(imageId);
    } else {
      this.mediaGallery.updateProductImage(imageId);
    }
  }

  /**
   * POSTs the kit variant + matched yarn variant to /cart/add.js as a single
   * multi-item request (Shopify's Ajax Cart API — the classic single-item
   * form-encoded /cart/add.js component-product-form uses in theme.js can't
   * express "two line items in one add"). Re-resolves the selection rather
   * than trusting stale state, since data-kit-invalid could theoretically be
   * out of date if this fires from something other than a user click later.
   */
  async handleAddToCart() {
    const { size, roles, isValid } = this.resolveSelection();
    if (!isValid || this.addToCartButton.disabled) return;

    this.addToCartButton.disabled = true;
    this.addToCartButton.classList.add("active");
    this.summary.classList.remove("product-form__error-message-text");

    const items = [
      { id: size.kitVariantId, quantity: 1 },
      ...roles.filter((r) => r.active).map((r) => ({ id: r.color.variantId, quantity: r.amount })),
    ];

    try {
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        this.showCartError(responseData.description || responseData.message || "Could not add this kit to your cart.");
        return;
      }

      document.dispatchEvent(
        new CustomEvent("theme-cart:updated", {
          detail: { shouldOpenCart: true },
        })
      );
    } catch (error) {
      this.showCartError("Could not add this kit to your cart. Check your connection and try again.");
    } finally {
      this.addToCartButton.classList.remove("active");
      // Re-resolve rather than blindly re-enabling — the button should only
      // come back if the selection is still valid.
      this.addToCartButton.disabled = !this.resolveSelection().isValid;
    }
  }

  /** Surfaces a failed /cart/add.js response in the summary area, reusing the same error styling as the invalid-state message. */
  showCartError(message) {
    this.summary.hidden = false;
    this.summary.classList.add("product-form__error-message-text");
    this.summary.textContent = message;
  }

  /**
   * Formats a cents value using the shop's own configured money_format
   * template (passed in via data-money-format, sourced from shop.money_format
   * in sections/kit-configurator.liquid) — the same format string that
   * drives every `| money` filter call elsewhere in this theme, including
   * snippets/price.liquid. This is Shopify's standard money-formatting
   * algorithm (the logic behind the `money` filter / the public money.js
   * theme utility), reimplemented here only because it has to run
   * client-side — the summed price * amount across active roles can't be
   * precomputed for every possible combination in Liquid without bloating
   * the embedded JSON.
   */
  formatMoney(cents) {
    if (!Number.isFinite(cents)) return "—";

    const placeholderMatch = this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = placeholderMatch ? placeholderMatch[1] : "amount";

    let formatted;
    switch (placeholder) {
      case "amount_no_decimals":
        formatted = this.formatWithDelimiters(cents, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        formatted = this.formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        formatted = this.formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount":
      default:
        formatted = this.formatWithDelimiters(cents, 2, ",", ".");
        break;
    }

    return placeholderMatch ? this.moneyFormat.replace(placeholderMatch[0], formatted) : formatted;
  }

  /** cents -> "1,234.56"-style string with the given thousands/decimal separators. */
  formatWithDelimiters(cents, precision, thousandsSeparator, decimalSeparator) {
    const fixed = (cents / 100).toFixed(precision);
    const [whole, decimals] = fixed.split(".");
    const wholeWithThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    return decimals ? `${wholeWithThousands}${decimalSeparator}${decimals}` : wholeWithThousands;
  }
}

if (!customElements.get("kit-configurator")) {
  customElements.define("kit-configurator", KitConfigurator);
}
